import { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Stack,
  Group,
  Button,
  Textarea,
  Progress,
} from '@mantine/core';
import { JobQuestion, JobQuestionResponse } from '@worker/types/myJobs.type';
import useNotification from '@hooks/useNotification';
import QuestionRenderer from './QuestionRenderer';

interface JobQuestionsModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (
    responses: JobQuestionResponse[],
    note?: string,
    files?: Map<number, File>
  ) => void;
  questions: JobQuestion[];
  isLoading?: boolean;
}

const MAX_NOTE_LENGTH = 600;

/**
 * Modal for answering job questions before applying
 * Supports all question types: open-field, multiple-choice, attach-file, and video
 */
function JobQuestionsModal({
  opened,
  onClose,
  onSubmit,
  questions,
  isLoading = false,
}: JobQuestionsModalProps) {
  const { showErrorNotification } = useNotification();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<Record<number, string>>({});
  const [files, setFiles] = useState<Map<number, File>>(new Map());
  const [applicationNote, setApplicationNote] = useState('');
  const [videoPreviewUrls, setVideoPreviewUrls] = useState<Map<number, string>>(
    new Map()
  );

  // Reset form state when modal opens for a new job
  useEffect(() => {
    if (opened) {
      setCurrentIndex(0);
      setResponses({});
      setFiles(new Map());
      setApplicationNote('');
      // Revoke any existing video URLs before clearing
      videoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
      setVideoPreviewUrls(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when opened changes
  }, [opened]);

  const hasQuestions = questions.length > 0;
  const totalSteps = questions.length + 1; // +1 for note step
  const currentQuestion = questions[currentIndex];
  const isNoteStep = currentIndex >= questions.length;
  const progress = ((currentIndex + 1) / totalSteps) * 100;

  const handleResponseChange = (questionId: number, value: string) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleFileChange = (questionId: number, file: File | null) => {
    // Handle video preview URL cleanup/creation
    setVideoPreviewUrls((prev) => {
      const newUrls = new Map(prev);
      const existingUrl = prev.get(questionId);
      if (existingUrl) {
        URL.revokeObjectURL(existingUrl);
        newUrls.delete(questionId);
      }
      if (file?.type.startsWith('video/')) {
        newUrls.set(questionId, URL.createObjectURL(file));
      }
      return newUrls;
    });

    setFiles((prev) => {
      const newFiles = new Map(prev);
      if (file) {
        const fileKey = `file_${questionId}`;
        setResponses((prevResponses) => ({
          ...prevResponses,
          [questionId]: fileKey,
        }));
        newFiles.set(questionId, file);
      } else {
        setResponses((prevResponses) => {
          const updated = { ...prevResponses };
          delete updated[questionId];
          return updated;
        });
        newFiles.delete(questionId);
      }
      return newFiles;
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const formattedResponses: JobQuestionResponse[] = Object.entries(responses)
      .filter(([, value]) => value.trim() !== '')
      .map(([questionId, data]) => ({
        questionId: Number(questionId),
        data,
      }));

    // Pass files if any were uploaded
    const filesMap = files.size > 0 ? files : undefined;
    onSubmit(formattedResponses, applicationNote || undefined, filesMap);
  };

  const canProceed = () => {
    if (isNoteStep) return true;
    if (!currentQuestion) return false;

    const hasResponse = responses[currentQuestion.id]?.trim();
    const hasFile = files.has(currentQuestion.id);

    // File questions require a file
    if (
      currentQuestion.type === 'attach-file' ||
      currentQuestion.type === 'video'
    ) {
      return hasFile;
    }

    // Text questions require text response
    return !!hasResponse;
  };

  // Simple note-only modal (no questions)
  if (!hasQuestions) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        centered
        withCloseButton
        title={
          <Text fw={700} size="xl">
            Share why you want to work this!
          </Text>
        }
      >
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            The recruiter sees this along with your profile + contact info
          </Text>

          <Textarea
            placeholder={`Max Length ${MAX_NOTE_LENGTH}`}
            value={applicationNote}
            onChange={(e) =>
              setApplicationNote(
                e.currentTarget.value.slice(0, MAX_NOTE_LENGTH)
              )
            }
            minRows={5}
            autosize
            maxRows={8}
          />

          <Button
            variant="filled"
            fullWidth
            onClick={handleSubmit}
            loading={isLoading}
          >
            Apply
          </Button>
        </Stack>
      </Modal>
    );
  }

  // Multi-step modal with questions
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          {isNoteStep ? 'Application Note' : 'Application Questions'}
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Step {currentIndex + 1} of {totalSteps}
            </Text>
            <Text size="sm" c="dimmed">
              {Math.round(progress)}%
            </Text>
          </Group>
          <Progress value={progress} size="sm" />
        </Stack>

        {isNoteStep ? (
          <Stack gap="md">
            <Text fw={600}>Share why you want to work this!</Text>
            <Text size="sm" c="dimmed">
              The recruiter sees this along with your profile + contact info
            </Text>
            <Textarea
              placeholder={`Max Length ${MAX_NOTE_LENGTH}`}
              value={applicationNote}
              onChange={(e) =>
                setApplicationNote(
                  e.currentTarget.value.slice(0, MAX_NOTE_LENGTH)
                )
              }
              minRows={4}
              autosize
              maxRows={8}
            />
          </Stack>
        ) : (
          currentQuestion && (
            <QuestionRenderer
              question={currentQuestion}
              response={responses[currentQuestion.id] || ''}
              file={files.get(currentQuestion.id) || null}
              videoPreviewUrl={videoPreviewUrls.get(currentQuestion.id)}
              onResponseChange={(value) =>
                handleResponseChange(currentQuestion.id, value)
              }
              onFileChange={(file) =>
                handleFileChange(currentQuestion.id, file)
              }
              onError={showErrorNotification}
            />
          )
        )}

        <Group justify="space-between" mt="md">
          <Button
            variant="default"
            onClick={currentIndex === 0 ? onClose : handleBack}
            disabled={isLoading}
          >
            {currentIndex === 0 ? 'Cancel' : 'Back'}
          </Button>

          {isNoteStep ? (
            <Button variant="filled" onClick={handleSubmit} loading={isLoading}>
              Apply
            </Button>
          ) : (
            <Button
              variant="filled"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

export default JobQuestionsModal;
